// src/Pages/Purchase/PurchaseData/ViewBills.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_URL } from "../../../config";
import "./ViewBills.css";          // ← now matches the real filename
// ... rest of the file stays exactly the same
const ViewBills = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBill = async () => {
      try {
        const res = await fetch(`${API_URL}/api/bills/${id}`, {
          credentials: "include",
        });
        if (!res.ok) {
          const text = await res.text();
          console.error("Server returned:", text.substring(0, 300));
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        if (data.success && data.data) {
          setBill(data.data);
        } else {
          console.error("Bill not found in response:", data);
          alert("Bill not found");
          navigate("/purchase/bills");
        }
      } catch (err) {
        console.error("Failed to fetch Bill:", err);
        alert("Error loading Bill");
        navigate("/purchase/bills");
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchBill();
  }, [id, navigate]);

  if (loading) return <div className="loading">Loading Bill...</div>;
  if (!bill) return <div className="error">Bill not found</div>;

  // Calculations
  const subtotal = bill.items.reduce(
    (sum, item) => sum + (item.amount || 0),
    0
  );
  const discountAmount =
    bill.discountType === "%"
      ? (subtotal * (bill.discountValue || 0)) / 100
      : bill.discountValue || 0;
  const tdsTcsAmount = bill.tdsTcs
    ? (subtotal * (bill.tdsTcsPercent || 0)) / 100
    : 0;
  const total =
    subtotal - discountAmount - tdsTcsAmount + (bill.adjustment || 0);

  const formatDate = (date) =>
    date ? new Date(date).toLocaleDateString("en-IN") : "—";

  return (
    <div className="view-bill-page">
      <div className="bill-print-container">
        {/* Header */}
        <div className="bill-header-section">
          <div className="company-details">
            <h1>Your Company Name</h1>
            <p>123 Business Street, City, State - 400001</p>
            <p>GSTIN: 22AAAAA0000A1Z5 | Email: info@yourcompany.com</p>
          </div>
          <div className="bill-title">
            <h1>BILL</h1>
            <h2>{bill.billNo}</h2>
          </div>
        </div>
        {/* Meta Info */}
        <div className="bill-meta-grid">
          <div>
            <strong>Bill Date</strong>
            <br />
            {formatDate(bill.date)}
          </div>
          <div>
            <strong>Due Date</strong>
            <br />
            {formatDate(bill.dueDate)}
          </div>
          <div>
            <strong>Reference #</strong>
            <br />
            {bill.referenceNo || "—"}
          </div>
          <div>
            <strong>Status</strong>
            <br />
            <span
              className={`status-badge ${
                bill.status === "OPEN"
                  ? "bg-blue-100 text-blue-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {bill.status || "DRAFT"}
            </span>
          </div>
        </div>
        {/* Vendor */}
        <div className="vendor-section">
          <div>
            <strong>Vendor</strong>
            <br />
            <strong>{bill.vendorName}</strong>
          </div>
        </div>
        {/* Subject */}
        {bill.subject && (
          <div className="subject-section">
            <strong>Subject</strong>
            <br />
            {bill.subject}
          </div>
        )}
        {/* Items Table */}
        <table className="items-table-print">
          <thead>
            <tr>
              <th>ITEM DETAILS</th>
              <th>ACCOUNT</th>
              <th className="text-center">QTY</th>
              <th className="text-right">RATE</th>
              <th>CUSTOMER DETAILS</th>
              <th className="text-right">AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {bill.items.map((item, i) => (
              <tr key={i}>
                <td>{item.description || "—"}</td>
                <td>{item.account || "—"}</td>
                <td className="text-center">
                  {item.qty || item.quantity || 0}
                </td>
                <td className="text-right">₹{(item.rate || 0).toFixed(2)}</td>
                <td>{item.customerDetails || "—"}</td>
                <td className="text-right">₹{(item.amount || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Totals */}
        <div className="totals-section">
          <div className="totals-left">
            <div>Subtotal</div>
            {discountAmount > 0 && (
              <div>
                Discount ({bill.discountValue}
                {bill.discountType})
              </div>
            )}
            {tdsTcsAmount > 0 && <div>TDS/TCS ({bill.tdsTcsPercent}%)</div>}
            {(bill.adjustment || 0) !== 0 && <div>Adjustment</div>}
            <div className="grand-total">Total Amount</div>
          </div>
          <div className="totals-right">
            <div>₹{subtotal.toFixed(2)}</div>
            {discountAmount > 0 && <div>-₹{discountAmount.toFixed(2)}</div>}
            {tdsTcsAmount > 0 && <div>-₹{tdsTcsAmount.toFixed(2)}</div>}
            {(bill.adjustment || 0) !== 0 && (
              <div>₹{(bill.adjustment || 0).toFixed(2)}</div>
            )}
            <div className="grand-total">₹{total.toFixed(2)}</div>
          </div>
        </div>
        {/* Notes */}
        {bill.customerNotes && (
          <div className="notes-section">
            <strong>Notes</strong>
            <div className="mt-2 text-sm whitespace-pre-line">
              {bill.customerNotes}
            </div>
          </div>
        )}
        {/* Action Buttons */}
        <div className="action-buttons-print">
          <button onClick={() => window.print()} className="print-btn">
            Print / Save as PDF
          </button>
          <button
            onClick={() => navigate("/purchase/bills")}
            className="back-btn"
          >
            ← Back to List
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewBills;
